/**
 * 합성 단계 사다리 패널 — 화면 하단에 11단계 전부를 작은 디스크로 보여줘
 * 클리어 조건(별 만들기) 을 항상 시각적으로 인지시키는 보조 패널.
 *
 * 각 칩은 등급 색상으로 채색되고 한글 이름을 라벨로 단다. 현재 런에서
 * 도달한 최고 등급(peak) 은 강조 링 + 펄스로 표시.
 *
 * 별도 목표 텍스트(🎯 목표 · 별 탄생시키기 / 최고 N/10) 는 상단 goal-pill
 * UI 가 담당 — 이 패널은 칩만 책임진다 (별 칸의 🎯 마커도 goal-pill 로
 * 옮겨져 중복 제거).
 */

import { TIER_TABLE, tierDisplayName } from "../game/tier.ts";

export interface TierLadderPanel {
  element: HTMLDivElement;
  /** 현재 런의 최고 등급 갱신 (peak 만 올라가도록 idempotent). */
  setPeakTier(tierIndex: number): void;
  /** 런 재시작 시 호출 — 모든 강조 해제. */
  reset(): void;
  detach(): void;
}

function hexToCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

export function attachTierLadderPanel(host: HTMLElement): TierLadderPanel {
  const element = document.createElement("div");
  element.dataset["testid"] = "tier-ladder-panel";
  // Bottom-center placement. The tier-ladder is the visual progression
  // strip; the textual goal callout (목표 · 별 탄생시키기 / 최고 N/10)
  // lives in a separate top-center goal-pill.
  // Mobile-first: tighter gap/padding/width on small screens.
  element.className =
    "pointer-events-none absolute inset-x-0 bottom-2 mx-auto flex max-w-[min(880px,96vw)] items-end justify-between gap-0.5 rounded-2xl bg-black/55 px-2 py-1.5 ring-1 ring-white/10 backdrop-blur-md sm:bottom-4 sm:gap-1 sm:px-3 sm:py-2";

  const chips: HTMLDivElement[] = [];

  for (const tier of TIER_TABLE) {
    const chip = document.createElement("div");
    chip.dataset["testid"] = `tier-chip-${tier.index}`;
    chip.className = "flex flex-col items-center gap-0.5 transition-transform duration-200";

    const dot = document.createElement("div");
    dot.dataset["testid"] = `tier-dot-${tier.index}`;
    // Smaller dots on narrow screens — the tier-row needs to fit even
    // at <640px width. Use CSS clamp for fluid sizing.
    const sizePx = 12 + tier.index * 1.5;
    dot.style.width = `${sizePx}px`;
    dot.style.height = `${sizePx}px`;
    dot.style.background = hexToCss(tier.color);
    dot.style.opacity = "0.32";
    dot.className = "rounded-full ring-2 ring-transparent shadow-md transition-all duration-200";

    const label = document.createElement("div");
    // Hide label text on very narrow screens so the chip row fits;
    // restore at sm: breakpoint.
    label.className = "hidden font-display text-[9px] tracking-tight text-slate-300/70 sm:block sm:text-[10px]";
    label.textContent = tierDisplayName(tier);

    chip.append(dot, label);
    element.appendChild(chip);
    chips.push(chip);
  }

  host.appendChild(element);

  let peak = -1;

  const render = (): void => {
    for (let i = 0; i < TIER_TABLE.length; i += 1) {
      const chip = chips[i]!;
      const dot = chip.querySelector<HTMLDivElement>(`[data-testid='tier-dot-${i}']`);
      const label = chip.querySelector<HTMLDivElement>("div + div");
      if (!dot) continue;
      const reached = i <= peak;
      dot.style.opacity = reached ? "1" : "0.28";
      dot.style.transform = reached ? "scale(1)" : "scale(0.85)";
      if (i === peak) {
        dot.style.boxShadow = `0 0 14px 3px ${hexToCss(TIER_TABLE[i]!.color)}`;
        dot.style.outline = "2px solid #ffffff";
      } else {
        dot.style.boxShadow = "0 1px 2px rgba(0,0,0,0.4)";
        dot.style.outline = "";
      }
      if (label) {
        label.style.color = reached ? "#e0e8f6" : "rgba(203, 213, 225, 0.55)";
      }
    }
  };

  const setPeakTier = (tierIndex: number): void => {
    if (tierIndex <= peak) return;
    peak = Math.max(0, Math.min(TIER_TABLE.length - 1, tierIndex));
    render();
    const chip = chips[peak];
    if (chip && typeof chip.animate === "function") {
      chip.animate(
        [
          { transform: "translateY(0) scale(1)" },
          { transform: "translateY(-6px) scale(1.18)" },
          { transform: "translateY(0) scale(1)" },
        ],
        { duration: 280, easing: "ease-out" },
      );
    }
  };

  const reset = (): void => {
    peak = -1;
    render();
  };

  const detach = (): void => {
    element.remove();
  };

  render();
  return { element, setPeakTier, reset, detach };
}
